import ThemedModal from '~/components/ThemedModal';
import { useEventTypesQuery } from '~/lib/SharedQueries';
import { useContext, useEffect, useMemo, useState } from 'react';
import Select from '~/components/Select';
import TextInput from '~/components/TextInput';
import EmailInput from '~/components/EmailInput';
import useSharedPostApi from '~/lib/useSharedPostApi';
import * as Toast from '~/lib/Toast';
import { FormContext } from '~/components/forms/FormContext';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import useClientTimezone from '~/public/lib/useClientTimezone.js';

export function useIsWaitlistEnabled(settings) {
    const { features } = useContext(FormContext);
    const isWaitlistFeatureEnabled = !!features?.waitlist;
    return !settings?.disableWaitlist && isWaitlistFeatureEnabled;
}

const AddedToWaitlist = ({ eventType }) => {
    return (
        <div className="flex flex-col items-center justify-center">
            <CheckCircleIcon className="w-20 h-20 themed-text" />
            <div className="text-lg font-bold mt-4">
                You’re on the waitlist.
            </div>
            <div className="text-gray-500 mt-4 text-center">
                {(!eventType || eventType.is_class) && (
                    <>
                        We will notify you once a spot opens up for{' '}
                        {eventType?.name || 'this'}.
                    </>
                )}
                {!!eventType && !eventType?.is_class && (
                    <>
                        We will notify you if any spots open up for{' '}
                        {eventType?.name} in the next month.
                    </>
                )}
            </div>
        </div>
    );
};

const AddToWaitlistModal = ({
    isOpen,
    onClose,
    eventType,
    bookableResource,
    start,
}) => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');

    const [hasAddedToWaitlist, setHasAddedToWaitlist] = useState(false);

    const { submission } = useContext(FormContext);
    const prefillClient = submission?.formData?.loggedInClient || {};

    const clientTz = useClientTimezone();

    useEffect(() => {
        if (isOpen && prefillClient) {
            setName(prefillClient.name);
            setEmail(prefillClient.email);
        }
    }, [isOpen]);

    useEffect(() => {
        setHasAddedToWaitlist(false);
    }, [eventType, bookableResource]);

    const { data: eventTypesData } = useEventTypesQuery();

    const eventTypeDetails = (eventTypesData || []).find(
        et => parseInt(et.id) === parseInt(eventType),
    );

    const { save, isSaving } = useSharedPostApi(`/waitlist/add`);

    function joinWaitlist(ev) {
        ev.preventDefault();
        ev.stopPropagation();

        save({
            eventType,
            bookableResource,
            start,
            url: document.location.href,
            timezone: clientTz,
            client: {
                name,
                email,
                phone,
            },
        }).then(result => {
            //Toast.success(result.message)
            setHasAddedToWaitlist(true);
            //onClose();
        });
    }

    return (
        <ThemedModal isOpen={isOpen} onClose={onClose}>
            {hasAddedToWaitlist && (
                <AddedToWaitlist eventType={eventTypeDetails} />
            )}
            {!hasAddedToWaitlist && (
                <>
                    <h2 className="mb-6">{eventTypeDetails?.name}</h2>

                    <form onSubmit={joinWaitlist}>
                        <TextInput
                            label="Name"
                            id="client.name"
                            onChange={setName}
                            value={name}
                            autoFocus={!name}
                            className="mb-4"
                        />

                        <div className="input-label">Email</div>
                        <EmailInput
                            id="client.email"
                            onChange={setEmail}
                            value={email}
                            className="mb-4"
                        />

                        <TextInput
                            type="text"
                            id="client.phone"
                            label="Phone"
                            onChange={setPhone}
                            value={phone}
                            className="mb-4"
                        />

                        <button className="form-button">
                            {isSaving ? 'Joining...' : 'Join Waitlist'}
                        </button>
                    </form>
                </>
            )}
        </ThemedModal>
    );
};

export default AddToWaitlistModal;
